import React, { useState, useRef, useEffect } from 'react';
import './MessageComposer.css';

const MessageComposer = ({ onSendMessage, onTyping, groupType, currentUser }) => {
  console.log('MessageComposer props:', { groupType, currentUser });
  
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const textareaRef = useRef(null);

  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    if (isTyping) {
      onTyping(true);
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        onTyping(false);
      }, 1000);
    }
  }, [isTyping, onTyping]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setMessage(value);
    
    if (value.trim()) {
      setIsTyping(true);
    } else {
      setIsTyping(false);
    }
  };

  const canSendMessage = message.trim().length > 0 || selectedFiles.length > 0;

  const handleSendMessage = () => {
    console.log('MessageComposer handleSendMessage called');
    console.log('Message:', message);
    console.log('Selected files:', selectedFiles);
    
    if (!message.trim() && selectedFiles.length === 0) {
      console.log('No message or files to send');
      return;
    }
    
    const messageType = selectedFiles.length > 0 ? 'file' : 'text';
    const attachments = selectedFiles.map(file => ({
      file_name: file.name,
      original_name: file.name,
      file_size: file.size,
      mime_type: file.type,
      file_url: URL.createObjectURL(file),
      file_type: file.type.startsWith('image/') ? 'image' : 'document'
    }));
    
    console.log('Calling onSendMessage with:', { message, messageType, attachments });
    onSendMessage(message, messageType, attachments);
    
    setMessage('');
    setSelectedFiles([]);
    setReplyTo(null);
    
    // Reset typing state
    setIsTyping(false);
    onTyping(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    console.log('Files selected:', files);
    
    // Validate file sizes (max 10MB per file)
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        alert(`檔案 ${file.name} 太大，最大支援 10MB`);
        return false;
      }
      return true;
    });
    
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      console.log('Valid files added:', validFiles);
    }
    
    // Reset the input value so the same file can be selected again
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getMessageTemplates = () => {
    console.log('Getting templates for group type:', groupType);
    const templates = {
      class: [
        { name: '功課提醒', content: '提醒：{subject}功課將於{deadline}到期，請按時提交。' },
        { name: '考試通知', content: '通知：{subject}考試將於{date}舉行，請準時到校。' },
        { name: '班級活動', content: '班級活動：{activity}將於{date}舉行，請踴躍參加。' }
      ],
      club: [
        { name: '活動提醒', content: '提醒：{activity}活動將於{date}舉行，請準時參加。' },
        { name: '練習通知', content: '通知：{activity}練習將於{date}舉行，請準時到場。' },
        { name: '比賽通知', content: '比賽通知：{activity}比賽將於{date}舉行，請做好準備。' }
      ],
      parent: [
        { name: '家長會通知', content: '通知：家長會將於{date}舉行，請準時參加。' },
        { name: '學生表現', content: '您的孩子{student}在{subject}方面表現良好，請繼續鼓勵。' },
        { name: '學校活動', content: '學校活動：{activity}將於{date}舉行，歡迎家長參加。' }
      ],
      general: [
        { name: '一般通知', content: '通知：{content}，請注意。' },
        { name: '系統維護', content: '系統維護通知：{date}將進行系統維護，請注意。' },
        { name: '緊急通知', content: '緊急通知：{content}，請立即處理。' }
      ]
    };
    
    const selectedTemplates = templates[groupType] || templates.general;
    console.log('Available templates:', selectedTemplates);
    return selectedTemplates;
  };

  const handleTemplateSelect = (template) => {
    console.log('Template selected:', template);
    console.log('Setting message to:', template.content);
    setMessage(template.content);
    setShowTemplates(false);
    // Focus on the textarea after selecting template
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getEmojis = () => {
    return [
      '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇',
      '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚',
      '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩',
      '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣',
      '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬',
      '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗',
      '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😯', '😦', '😧',
      '😮', '😲', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮',
      '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '💩', '👻', '💀', '☠️',
      '👽', '👾', '🤖', '😺', '😸', '😹', '😻', '😼', '😽', '🙀',
      '😿', '😾', '🙈', '🙉', '🙊', '💌', '💘', '💝', '💖', '💗',
      '💓', '💞', '💕', '💟', '❣️', '💔', '❤️', '🧡', '💛', '💚',
      '💙', '💜', '🖤', '💯', '💢', '💥', '💫', '💦', '💨', '🕳️',
      '💬', '🗨️', '🗯️', '💭', '💤', '💋', '💯', '💢', '💥', '💫'
    ];
  };

  const handleEmojiSelect = (emoji) => {
    console.log('Emoji selected:', emoji);
    setMessage(prev => prev + emoji);
    setShowEmojis(false);
    // Focus on the textarea after selecting emoji
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };



  return (
    <div className="message-composer">
      {/* Reply indicator */}
      {replyTo && (
        <div className="reply-indicator">
          <span className="reply-icon">↩️</span>
          <span className="reply-text">回覆 {replyTo.sender}</span>
          <button 
            className="btn-remove-reply"
            onClick={() => setReplyTo(null)}
          >
            ✕
          </button>
        </div>
      )}

      {/* File attachments preview */}
      {selectedFiles.length > 0 && (
        <div className="attachments-preview">
          {selectedFiles.map((file, index) => (
            <div key={index} className="attachment-preview-item">
              <div className="attachment-info">
                <span className="file-icon">
                  {file.type.startsWith('image/') ? '🖼️' : '📎'}
                </span>
                <span className="file-name">{file.name}</span>
                <span className="file-size">{formatFileSize(file.size)}</span>
              </div>
              <button 
                className="btn-remove-file"
                onClick={() => removeFile(index)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Message input area */}
      <div className="composer-input-area">
        <div className="composer-controls">
          <button 
            className="control-btn"
            onClick={() => {
              console.log('Template button clicked, current state:', showTemplates);
              console.log('Group type:', groupType);
              setShowTemplates(!showTemplates);
            }}
            title="訊息範本"
          >
            📝
          </button>
          
          <button 
            className="control-btn"
            onClick={() => {
              console.log('Attachment button clicked, current state:', showAttachments);
              setShowAttachments(!showAttachments);
            }}
            title="附加檔案"
          >
            📎
          </button>
          
          <button 
            className="control-btn"
            onClick={() => {
              console.log('Emoji button clicked, current state:', showEmojis);
              setShowEmojis(!showEmojis);
            }}
            title="表情符號"
          >
            😊
          </button>
        </div>

        <div className="message-input-container">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="輸入訊息..."
            className="message-input"
            rows="1"
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
          />
        </div>

        <button
          className={`send-button ${canSendMessage ? 'active' : 'disabled'}`}
          onClick={handleSendMessage}
          disabled={!canSendMessage}
        >
          ➤
        </button>
      </div>

      {/* Message templates */}
      {showTemplates && (
        <div className="message-templates">
          <div className="templates-header">
            <h4>訊息範本</h4>
            <button 
              className="btn-close-templates"
              onClick={() => setShowTemplates(false)}
            >
              ✕
            </button>
          </div>
          <div className="templates-list">
            {getMessageTemplates().map((template, index) => (
                              <button
                  key={index}
                  className="template-item"
                  onClick={() => handleTemplateSelect(template)}
                >
                <span className="template-name">{template.name}</span>
                <span className="template-preview">{template.content.substring(0, 30)}...</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* File attachment input */}
      {showAttachments && (
        <div className="file-attachment-panel">
          <div className="attachment-header">
            <h4>附加檔案</h4>
            <button 
              className="btn-close-attachments"
              onClick={() => setShowAttachments(false)}
            >
              ✕
            </button>
          </div>
          
          <div className="attachment-options">
            <label className="file-upload-btn">
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                style={{ display: 'none' }}
              />
              <span>選擇檔案</span>
            </label>
            
            <div className="file-types-info">
              <p>支援的檔案類型：</p>
              <p>圖片 (JPG, PNG, GIF), 文件 (PDF, DOC, XLS), 文字檔</p>
            </div>
          </div>
        </div>
      )}

      {/* Emoji picker */}
      {showEmojis && (
        <div className="emoji-picker-panel">
          <div className="emoji-header">
            <h4>表情符號</h4>
            <button 
              className="btn-close-emojis"
              onClick={() => setShowEmojis(false)}
            >
              ✕
            </button>
          </div>
          <div className="emoji-grid">
            {getEmojis().map((emoji, index) => (
              <button
                key={index}
                className="emoji-item"
                onClick={() => handleEmojiSelect(emoji)}
                title={emoji}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageComposer;
